import { getDocument } from '@atlaskit/browser-apis';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type {
	MultiFormatContentType,
	StreamingCommand,
} from '../../aiStreamingOrchestratorPluginType';
import { collectAllLocalIds } from './find-node-by-local-id';

export type TargetProvenance = 'session_start' | 'earlier_tool_call' | 'never_seen';

type LogicalCall = {
	commandLocalIds: Set<string>;
	ordinal: number;
	rawSourceIsAuthoritative: boolean;
	snapshotLocalIds: Set<string>;
};

type AdfNode = {
	attrs?: { localId?: unknown };
	content?: unknown;
	type?: unknown;
};

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function collectLocalIdsFromAdf(value: string): Set<string> | undefined {
	let adf: unknown;
	try {
		adf = JSON.parse(value);
	} catch {
		return undefined;
	}

	if (!isObject(adf) || typeof adf.type !== 'string') {
		return undefined;
	}
	if (adf.type === 'doc' && !Array.isArray(adf.content)) {
		return undefined;
	}

	const localIds = new Set<string>();
	const pending: unknown[] = [];
	if (adf.type === 'doc') {
		if (Array.isArray(adf.content)) {
			for (const child of adf.content) {
				pending.push(child);
			}
		}
	} else {
		pending.push(adf);
	}

	while (pending.length > 0) {
		const candidate = pending.pop();
		if (!isObject(candidate) || typeof candidate.type !== 'string') {
			continue;
		}
		const node = candidate as AdfNode;
		const localId = isObject(node.attrs) ? node.attrs.localId : undefined;
		if (typeof localId === 'string' && localId.length > 0) {
			localIds.add(localId);
		}

		if (Array.isArray(node.content)) {
			for (const child of node.content) {
				pending.push(child);
			}
		}
	}

	return localIds;
}

function collectLocalIdsFromHtml(value: string): Set<string> {
	const localIds = new Set<string>();
	const doc = getDocument();
	if (!doc) {
		return localIds;
	}

	const template = doc.createElement('template');
	template.innerHTML = value;
	for (const node of template.content.querySelectorAll('[data-local-id]')) {
		const localId = node.getAttribute('data-local-id');
		if (localId) {
			localIds.add(localId);
		}
	}
	return localIds;
}

function addCommandLocalIds(record: LogicalCall, command: StreamingCommand): void {
	const addLocalId = (localId: unknown) => {
		if (typeof localId === 'string' && localId.length > 0) {
			record.commandLocalIds.add(localId);
		}
	};

	switch (command.type) {
		case 'setAttrs':
			addLocalId(command.attrs.localId);
			break;
		case 'wrap':
		case 'wrapNode':
			addLocalId(command.wrapperAttrs.localId);
			break;
	}
}

/**
 * Tracks which tool call first supplied a target localId, using the initial
 * document, command attributes, and authoritative streamed source snapshots.
 */
export class TargetProvenanceTracker {
	private readonly sessionStartLocalIds: Set<string>;
	private readonly calls: LogicalCall[] = [];
	private readonly callsByActionId = new Map<string, LogicalCall>();
	private readonly callsByToolCallId = new Map<string, LogicalCall>();
	private nextOrdinal = 0;

	constructor(initialDoc: PMNode) {
		this.sessionStartLocalIds = collectAllLocalIds(initialDoc);
	}

	register(actionId: string, command: StreamingCommand, toolCallId?: string): void {
		const existingCall = this.callsByActionId.get(actionId);
		const aliasedCall = toolCallId ? this.callsByToolCallId.get(toolCallId) : undefined;
		const call = existingCall ??
			aliasedCall ?? {
				ordinal: this.nextOrdinal++,
				commandLocalIds: new Set<string>(),
				rawSourceIsAuthoritative: false,
				snapshotLocalIds: new Set<string>(),
			};

		if (!existingCall && !aliasedCall) {
			this.calls.push(call);
		}

		addCommandLocalIds(call, command);
		this.callsByActionId.set(actionId, call);
		if (toolCallId) {
			this.callsByToolCallId.set(toolCallId, call);
		}
	}

	markForceAborted(actionId: string, toolCallId: string): void {
		const call = this.callsByActionId.get(actionId);
		if (call) {
			this.callsByToolCallId.set(toolCallId, call);
		}
	}

	recordValue(actionId: string, value: string): void {
		const call = this.callsByActionId.get(actionId);
		if (!call || call.rawSourceIsAuthoritative) {
			return;
		}

		const snapshotLocalIds = collectLocalIdsFromAdf(value);
		if (snapshotLocalIds) {
			call.snapshotLocalIds = snapshotLocalIds;
		}
	}

	recordSourceValue(
		actionIdOrToolCallId: string,
		value: string,
		contentType: MultiFormatContentType,
	): void {
		const call =
			this.callsByActionId.get(actionIdOrToolCallId) ??
			this.callsByToolCallId.get(actionIdOrToolCallId);
		if (!call) {
			return;
		}

		if (!call.rawSourceIsAuthoritative) {
			call.snapshotLocalIds = new Set<string>();
		}
		call.rawSourceIsAuthoritative = true;
		switch (contentType) {
			case 'application/adf+json': {
				const snapshotLocalIds = collectLocalIdsFromAdf(value);
				if (snapshotLocalIds) {
					call.snapshotLocalIds = snapshotLocalIds;
				}
				break;
			}
			case 'text/html':
				call.snapshotLocalIds = collectLocalIdsFromHtml(value);
				break;
			case 'text/markdown':
				call.snapshotLocalIds = new Set<string>();
				break;
		}
	}

	classify(actionId: string, targetLocalId: string | undefined): TargetProvenance | undefined {
		const call = this.callsByActionId.get(actionId);
		if (!call || !targetLocalId) {
			return undefined;
		}

		if (this.sessionStartLocalIds.has(targetLocalId)) {
			return 'session_start';
		}

		for (const earlierCall of this.calls) {
			if (
				earlierCall.ordinal < call.ordinal &&
				(earlierCall.commandLocalIds.has(targetLocalId) ||
					earlierCall.snapshotLocalIds.has(targetLocalId))
			) {
				return 'earlier_tool_call';
			}
		}

		return 'never_seen';
	}
}
